// #5: Store Opening / Closing report. Reconciles each store item over a date
// range (Opening + Purchase − Consumption − Wastage = Closing) straight from
// the stock ledger. Export: Excel + Print/PDF. Read-only.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import config from '../../config';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
const token = sessionStorage.getItem('token');
const auth = { Authorization: `Bearer ${token}` };

const today = () => new Date().toISOString().slice(0, 10);

const COLS = [
  { key: 'itemName', label: 'Item', num: false },
  { key: 'unit', label: 'Unit', num: false },
  { key: 'opening', label: 'Opening', num: true },
  { key: 'purchaseIn', label: 'Purchase In', num: true },
  { key: 'transferOut', label: 'Transfer', num: true },
  { key: 'consumption', label: 'Consumption', num: true },
  { key: 'salesQty', label: 'Sales', num: true },
  { key: 'wastage', label: 'Wastage/Adj', num: true },
  { key: 'closing', label: 'Closing', num: true },
  { key: 'openingValue', label: 'Open Value', num: true },
  { key: 'closingValue', label: 'Close Value', num: true },
];

export default function StockOpeningClosingReport() {
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categoryId, setCategoryId] = useState('');
  const [categories, setCategories] = useState([]);

  const load = useCallback(async () => {
    if (!company?.id) return;
    setLoading(true);
    try {
      const url =
        `${config.API_BASE_URL}/stockreport/opening-closing/${company.id}` +
        `?from=${from}&to=${to}${search ? `&search=${encodeURIComponent(search)}` : ''}${categoryId ? `&categoryId=${categoryId}` : ''}`;
      const res = await fetch(url, { headers: auth });
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      setRows(data.rows || []);
      setTotals(data.totals || null);
    } catch (e) {
      toast.error('Could not load report');
      setRows([]);
      setTotals(null);
    } finally {
      setLoading(false);
    }
  }, [from, to, search, categoryId]);

  useEffect(() => {
    load();
    if (company?.id) {
      fetch(`${config.API_BASE_URL}/Home/GetAllCategory/${company.id}`, { headers: auth })
        .then((r) => r.json())
        .then((d) => setCategories(Array.isArray(d) ? d : d?.data || []))
        .catch(() => setCategories([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 3 });

  const exportExcel = () => {
    if (!rows.length) return toast.info('No data to export');
    const sheet = rows.map((r) => ({
      Item: r.itemName,
      Unit: r.unit,
      Opening: r.opening,
      'Purchase In': r.purchaseIn,
      'Transfer Out': r.transferOut,
      Sales: r.salesQty,
      Consumption: r.consumption,
      'Wastage/Adj': r.wastage,
      Closing: r.closing,
      'Open Value': r.openingValue,
      'Close Value': r.closingValue,
    }));
    const ws = XLSX.utils.json_to_sheet(sheet);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'OpeningClosing');
    XLSX.writeFile(wb, `Opening_Closing_${from}_to_${to}.xlsx`);
  };

  const printReport = () => {
    if (!rows.length) return toast.info('No data to print');
    const head = COLS.map((c) => `<th style="text-align:${c.num ? 'right' : 'left'}">${c.label}</th>`).join('');
    const body = rows
      .map(
        (r) =>
          `<tr>${COLS.map(
            (c) => `<td style="text-align:${c.num ? 'right' : 'left'}">${c.num ? fmt(r[c.key]) : r[c.key] ?? ''}</td>`,
          ).join('')}</tr>`,
      )
      .join('');
    const totalRow = totals
      ? `<tr class="tot"><td>TOTAL</td><td></td>${COLS.filter((c) => c.num).map((c) => `<td style="text-align:right">${fmt(totals[c.key])}</td>`).join('')}</tr>`
      : '';
    const w = window.open('', '_blank');
    w.document.write(`
      <html><head><title>Opening / Closing Report</title>
      <style>
        body{font-family:Arial,sans-serif;padding:24px;color:#0a2540}
        h2{margin:0 0 4px}
        .sub{color:#555;margin-bottom:16px;font-size:13px}
        table{width:100%;border-collapse:collapse;font-size:12px}
        th,td{border:1px solid #ccc;padding:6px 8px}
        th{background:#0a2540;color:#fff}
        .tot td{font-weight:bold;background:#eef2ff}
      </style></head><body>
      <h2>${company?.companyname || 'Store'} — Opening / Closing Report</h2>
      <div class="sub">Period: ${from} to ${to}</div>
      <table><thead><tr>${head}</tr></thead><tbody>${body}${totalRow}</tbody></table>
      </body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  const totalCells = useMemo(() => COLS.filter((c) => c.num).map((c) => c.key), []);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      {/* Hero header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 3,
          borderRadius: 3,
          color: '#fff',
          position: 'relative',
          overflow: 'hidden',
          background:
            'linear-gradient(135deg,#0b1120 0%,#0f172a 40%,#1e3a8a 100%)',
        }}>
        <Box sx={{ position: 'absolute', top: -40, right: -30, width: 180, height: 180, borderRadius: '50%', background: 'radial-gradient(circle, rgba(96,165,250,0.20), transparent 70%)', pointerEvents: 'none' }} />
        <Typography
          variant='h5'
          fontWeight={800}
          sx={{ position: 'relative', color: '#fff' }}>
          📦 Store Opening / Closing Report
        </Typography>
        <Typography
          variant='body2'
          sx={{ mt: 0.5, position: 'relative', color: '#cbd5e1' }}>
          Opening + Purchase − Consumption − Wastage = Closing (from stock ledger)
        </Typography>
      </Paper>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems='center'>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              size='small'
              type='date'
              label='From'
              InputLabelProps={{ shrink: true }}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              size='small'
              type='date'
              label='To'
              InputLabelProps={{ shrink: true }}
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size='small'
              label='Search item'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField select fullWidth size='small' label='Department (Category)'
              value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <MenuItem value=''>All</MenuItem>
              {categories.map((c) => {
                const cid = c.id ?? c.Id ?? c.categoryId;
                const cn = c.categoryName ?? c.CategoryName ?? c.name ?? '';
                return (<MenuItem key={cid} value={cid}>{cn}</MenuItem>);
              })}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box display='flex' gap={1} flexWrap='wrap'>
              <Button variant='contained' startIcon={<RefreshIcon />} onClick={load}>
                Load
              </Button>
              <Button variant='outlined' color='success' startIcon={<DownloadIcon />} onClick={exportExcel}>
                Excel
              </Button>
              <Button variant='outlined' startIcon={<PrintIcon />} onClick={printReport}>
                Print
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: '65vh' }}>
            <Table stickyHeader size='small'>
              <TableHead>
                <TableRow>
                  {COLS.map((c) => (
                    <TableCell key={c.key} align={c.num ? 'right' : 'left'} sx={{ fontWeight: 700 }}>
                      {c.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={COLS.length} align='center' sx={{ py: 4, color: 'text.secondary' }}>
                      No movement in this period.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => (
                    <TableRow key={r.storeItemId} hover>
                      {COLS.map((c) => (
                        <TableCell key={c.key} align={c.num ? 'right' : 'left'}>
                          {c.num ? fmt(r[c.key]) : r[c.key]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
                {totals && rows.length > 0 && (
                  <TableRow sx={{ '& td': { fontWeight: 800, background: '#eef2ff' } }}>
                    <TableCell>TOTAL</TableCell>
                    <TableCell />
                    {totalCells.map((k) => (
                      <TableCell key={k} align='right'>
                        {fmt(totals[k])}
                      </TableCell>
                    ))}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
